"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import AppImage from "@/components/ui/AppImage";
import Footer from "@/components/Footer";

// ─── Data ────────────────────────────────────────────────────────────────────

const services = [
  {
    id: "waste",
    icon: "🗑️",
    accentColor: "#EF4444",
    lightColor: "rgba(239,68,68,0.1)",
    borderColor: "rgba(239,68,68,0.2)",
    label: "Category 01",
    title: "Waste Removal",
    subtitle: "MARPOL-Compliant Disposal",
    shortDesc:
      "Complete sludge, slop, bilge, and garbage removal from vessels — executed to strict MARPOL and IMO standards with zero environmental impact.",
    fullDesc:
      "BlueSea Marine Services operates a fully certified waste management fleet capable of handling every category of shipboard waste. Our MARPOL-compliant operations cover sludge and slop discharge, bilge water removal, garbage collection, and condemned stores disposal. We work around vessel schedules to minimise port turnaround time while ensuring full regulatory compliance with port state control requirements.",
    features: [
      "Sludge & Slop Discharge",
      "Bilge Water Removal",
      "Garbage & Condemned Stores",
      "All Tank Cleaning",
      "Pollution-Free Disposal",
      "MARPOL ORB Documentation",
      "24/7 Emergency Response",
      "Port State Control Compliance",
    ],
    packages: [
      {
        name: "Basic",
        price: "On Request",
        tag: "Small Vessels",
        features: [
          "Bilge water removal",
          "Garbage collection",
          "Standard documentation",
          "Business hours support",
        ],
        highlight: false,
      },
      {
        name: "Standard",
        price: "On Request",
        tag: "Most Popular",
        features: [
          "Sludge & slop discharge",
          "Bilge water removal",
          "Garbage & condemned stores",
          "MARPOL ORB documentation",
          "Extended hours support",
        ],
        highlight: true,
      },
      {
        name: "Premium",
        price: "On Request",
        tag: "Full Fleet",
        features: [
          "All Standard features",
          "Full tank cleaning",
          "24/7 emergency response",
          "Dedicated account manager",
          "Priority scheduling",
          "Compliance audit support",
        ],
        highlight: false,
      },
    ],
    caseStudy: {
      title: "Bulk Carrier Sludge Emergency – Nhava Sheva",
      challenge:
        "A 75,000 DWT bulk carrier arrived at Nhava Sheva with critically high sludge levels, risking port state detention and a 48-hour delay to the vessel's schedule.",
      solution:
        "BlueSea deployed a dedicated sludge barge within 4 hours of notification. Our team coordinated directly with port authorities to obtain emergency permits and completed the full discharge within 6 hours.",
      result: "Vessel departed on schedule. Zero PSC deficiencies. Full MARPOL documentation provided within 2 hours of completion.",
      stats: [
        { label: "Response Time", value: "4 hrs" },
        { label: "Sludge Removed", value: "120 MT" },
        { label: "Delay Avoided", value: "48 hrs" },
      ],
    },
  },
  {
    id: "supply",
    icon: "📦",
    accentColor: "#2196F3",
    lightColor: "rgba(33,150,243,0.1)",
    borderColor: "rgba(33,150,243,0.2)",
    label: "Category 02",
    title: "Supply Services",
    subtitle: "End-to-End Vessel Provisioning",
    shortDesc:
      "From provisions to engine room stores, portable fire extinguishers to bunker supply — we stock everything your vessel needs at port.",
    fullDesc:
      "Our supply division maintains deep relationships with certified vendors across India, UAE, Bangladesh, and Sri Lanka to ensure rapid, cost-effective delivery of all vessel requirements. Whether you need fresh provisions, deck and engine room stores, safety equipment, or bunker fuel, BlueSea acts as your single point of contact — eliminating the complexity of managing multiple suppliers across different ports.",
    features: [
      "Provision & Deck Stores",
      "Engine Room Stores",
      "Bunker Supply",
      "Portable Fire Extinguishers",
      "Machinery (New & Second-Hand)",
      "Safety Equipment",
      "Spare Parts Sourcing",
      "Cold Chain Provisions",
    ],
    packages: [
      {
        name: "Basic",
        price: "On Request",
        tag: "Essentials",
        features: [
          "Provision supply",
          "Deck stores",
          "Standard lead time",
          "Single port delivery",
        ],
        highlight: false,
      },
      {
        name: "Standard",
        price: "On Request",
        tag: "Most Popular",
        features: [
          "Provisions & deck stores",
          "Engine room stores",
          "Safety equipment",
          "Expedited delivery",
          "Multi-port coordination",
        ],
        highlight: true,
      },
      {
        name: "Premium",
        price: "On Request",
        tag: "Full Service",
        features: [
          "All Standard features",
          "Bunker supply",
          "Spare parts sourcing",
          "Cold chain provisions",
          "Dedicated procurement team",
          "24/7 emergency supply",
        ],
        highlight: false,
      },
    ],
    caseStudy: {
      title: "Emergency Spare Parts – Jebel Ali, UAE",
      challenge:
        "A container vessel experienced a critical engine room failure at Jebel Ali requiring specialised spare parts unavailable locally. The vessel faced a potential 5-day delay.",
      solution:
        "BlueSea's UAE supply team activated its global vendor network, sourcing the required parts from a certified supplier in Singapore. Parts were air-freighted and cleared through customs within 36 hours.",
      result: "Vessel resumed operations 3 days ahead of the worst-case estimate. Total cost was 40% below the owner\'s initial budget.",
      stats: [
        { label: "Parts Sourced", value: "36 hrs" },
        { label: "Cost Saving", value: "40%" },
        { label: "Delay Avoided", value: "3 days" },
      ],
    },
  },
  {
    id: "survey",
    icon: "🔍",
    accentColor: "#C8A84B",
    lightColor: "rgba(200,168,75,0.1)",
    borderColor: "rgba(200,168,75,0.2)",
    label: "Category 03",
    title: "Survey Services",
    subtitle: "Independent Marine Inspection",
    shortDesc:
      "Accredited survey services covering cargo, vessel condition, insurance, and draught — providing independent verification your stakeholders trust.",
    fullDesc:
      "Our panel of accredited marine surveyors provides fully independent inspection and verification services for ship owners, charterers, P&I clubs, and cargo interests. From pre-loading hatch fitness inspections to post-discharge cargo damage assessments, our surveyors deliver detailed, legally defensible reports that protect your interests and satisfy the requirements of insurers and arbitrators worldwide.",
    features: [
      "Insurance & Draught Survey",
      "Break Bulk & Cargo Damage",
      "Hatch Fitness Inspection",
      "On/Off Hire Condition Survey",
      "Vessel Valuation Survey",
      "P&I Club Surveys",
      "Pre-Purchase Inspection",
      "Bunker Quantity Survey",
    ],
    packages: [
      {
        name: "Basic",
        price: "On Request",
        tag: "Single Survey",
        features: [
          "Single survey type",
          "Standard report",
          "5-day turnaround",
          "Digital delivery",
        ],
        highlight: false,
      },
      {
        name: "Standard",
        price: "On Request",
        tag: "Most Popular",
        features: [
          "Up to 3 survey types",
          "Detailed report with photos",
          "48-hour turnaround",
          "P&I club accepted",
          "Expert witness available",
        ],
        highlight: true,
      },
      {
        name: "Premium",
        price: "On Request",
        tag: "Full Coverage",
        features: [
          "Unlimited survey types",
          "Comprehensive legal report",
          "24-hour turnaround",
          "Expert witness included",
          "Arbitration support",
          "Multi-port coordination",
        ],
        highlight: false,
      },
    ],
    caseStudy: {
      title: "Cargo Damage Dispute – Kolkata Port",
      challenge:
        "A cargo interest reported significant damage to a steel coil shipment at Kolkata. The ship owner disputed liability, and the matter was heading to arbitration with $2.4M at stake.",
      solution:
        "BlueSea's surveyor conducted an independent joint survey with both parties present, documenting pre-existing damage through photographic evidence and comparing it against the bill of lading condition notes.",
      result: "The independent survey report was accepted by both parties' P&I clubs, enabling an out-of-court settlement within 3 weeks and avoiding costly arbitration.",
      stats: [
        { label: "Dispute Value", value: "$2.4M" },
        { label: "Settlement", value: "3 wks" },
        { label: "Arbitration", value: "Avoided" },
      ],
    },
  },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function useVisible() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return { ref, visible };
}

interface PricingCardProps {
  pkg: { name: string; price: string; tag: string; features: string[]; highlight: boolean };
  accentColor: string;
  lightColor: string;
  borderColor: string;
}

function PricingCard({ pkg, accentColor, lightColor, borderColor }: PricingCardProps) {
  return (
    <div
      className={`relative flex flex-col rounded-2xl p-7 transition-all duration-300 hover:-translate-y-1 ${
        pkg.highlight ? "ring-2" : ""
      }`}
      style={{
        background: pkg.highlight
          ? `linear-gradient(135deg, rgba(10,22,40,0.98), rgba(15,32,64,0.95))`
          : `rgba(10,22,40,0.7)`,
        border: pkg.highlight ? `1px solid ${accentColor}` : `1px solid ${borderColor}`,
        boxShadow: pkg.highlight ? `0 0 40px ${accentColor}22` : "none",
        ringColor: accentColor,
      }}
    >
      {pkg.highlight && (
        <div
          className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest text-navy-900"
          style={{ background: accentColor }}
        >
          {pkg.tag}
        </div>
      )}
      <div className="mb-5">
        <p className="text-[12px] uppercase tracking-widest font-bold mb-1" style={{ color: accentColor }}>
          {pkg.name}
        </p>
        <p className="text-2xl font-bold text-white">{pkg.price}</p>
        {!pkg.highlight && (
          <p className="text-[12px] text-[#8BA3BC] mt-1">{pkg.tag}</p>
        )}
      </div>
      <ul className="space-y-3 flex-1">
        {pkg.features.map((f, i) => (
          <li key={i} className="flex items-start gap-3">
            <span
              className="mt-1 w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 text-[10px]"
              style={{ background: lightColor, color: accentColor }}
            >
              ✓
            </span>
            <span className="text-[13px] text-[#8BA3BC]">{f}</span>
          </li>
        ))}
      </ul>
      <a
        href="/home-page#contact"
        className="mt-7 w-full flex items-center justify-center gap-2 py-3 rounded-xl text-[13px] font-bold uppercase tracking-widest transition-all duration-300"
        style={{
          background: pkg.highlight ? accentColor : lightColor,
          color: pkg.highlight ? "#0A1628" : accentColor,
          border: `1px solid ${borderColor}`,
        }}
      >
        Get Quote
      </a>
    </div>
  );
}

interface ServiceSectionProps {
  service: (typeof services)[0];
  index: number;
}

function ServiceSection({ service, index }: ServiceSectionProps) {
  const { ref, visible } = useVisible();
  const [activeTab, setActiveTab] = useState<"overview" | "pricing" | "case-study">("overview");

  return (
    <section
      ref={ref}
      id={service.id}
      className="py-24 md:py-32 relative overflow-hidden"
      style={{
        background:
          index % 2 === 0
            ? "rgba(10,22,40,0.5)"
            : "rgba(15,32,64,0.3)",
      }}
    >
      {/* Ambient glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] rounded-full blur-[120px] pointer-events-none opacity-10"
        style={{ background: service.accentColor }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className={`mb-14 ${visible ? "animate-fade-up" : "opacity-0"}`}>
          <div className="flex items-center gap-4 mb-4">
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center text-xl"
              style={{ background: service.lightColor, border: `1px solid ${service.borderColor}` }}
            >
              {service.icon}
            </div>
            <span className="text-[11px] uppercase tracking-[0.4em] font-bold" style={{ color: service.accentColor }}>
              {service.label}
            </span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-white mb-3">
            {service.title}
          </h2>
          <p className="text-[14px] font-semibold uppercase tracking-widest mb-6" style={{ color: service.accentColor }}>
            {service.subtitle}
          </p>
          <p className="text-[#8BA3BC] max-w-2xl leading-relaxed text-[16px]">{service.fullDesc}</p>
        </div>

        {/* Tab navigation */}
        <div className={`flex gap-2 mb-10 flex-wrap ${visible ? "animate-fade-up" : "opacity-0"}`}>
          {(["overview", "pricing", "case-study"] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className="px-5 py-2.5 rounded-full text-[12px] font-bold uppercase tracking-widest transition-all duration-300"
              style={{
                background: activeTab === tab ? service.accentColor : service.lightColor,
                color: activeTab === tab ? "#0A1628" : service.accentColor,
                border: `1px solid ${service.borderColor}`,
              }}
            >
              {tab === "case-study" ? "Case Study" : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === "overview" && (
          <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 ${visible ? "animate-scale-in" : "opacity-0"}`}>
            {service.features.map((feat, i) => (
              <div
                key={i}
                className="flex items-center gap-4 p-5 rounded-2xl"
                style={{
                  background: service.lightColor,
                  border: `1px solid ${service.borderColor}`,
                }}
              >
                <div
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ background: service.accentColor }}
                />
                <span className="text-[14px] text-white font-medium">{feat}</span>
              </div>
            ))}
          </div>
        )}

        {activeTab === "pricing" && (
          <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 ${visible ? "animate-scale-in" : "opacity-0"}`}>
            {service.packages.map((pkg, i) => (
              <PricingCard
                key={i}
                pkg={pkg}
                accentColor={service.accentColor}
                lightColor={service.lightColor}
                borderColor={service.borderColor}
              />
            ))}
          </div>
        )}

        {activeTab === "case-study" && (
          <div className={`${visible ? "animate-scale-in" : "opacity-0"}`}>
            <div
              className="rounded-3xl p-8 md:p-12"
              style={{
                background: `linear-gradient(135deg, rgba(10,22,40,0.98), rgba(15,32,64,0.95))`,
                border: `1px solid ${service.borderColor}`,
              }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div
                  className="px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest"
                  style={{ background: service.lightColor, color: service.accentColor }}
                >
                  Case Study
                </div>
              </div>
              <h3 className="font-display text-2xl md:text-3xl font-bold text-white mb-8">
                {service.caseStudy.title}
              </h3>

              {/* Stats row */}
              <div className="grid grid-cols-3 gap-4 mb-10">
                {service.caseStudy.stats.map((stat, i) => (
                  <div
                    key={i}
                    className="text-center p-5 rounded-2xl"
                    style={{ background: service.lightColor, border: `1px solid ${service.borderColor}` }}
                  >
                    <p className="font-display text-3xl font-bold" style={{ color: service.accentColor }}>
                      {stat.value}
                    </p>
                    <p className="text-[12px] text-[#8BA3BC] uppercase tracking-widest mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  { label: "Challenge", text: service.caseStudy.challenge },
                  { label: "Solution", text: service.caseStudy.solution },
                  { label: "Result", text: service.caseStudy.result },
                ].map((item, i) => (
                  <div key={i}>
                    <div className="flex items-center gap-2 mb-3">
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold"
                        style={{ background: service.accentColor, color: "#0A1628" }}
                      >
                        {i + 1}
                      </div>
                      <span className="text-[12px] uppercase tracking-widest font-bold" style={{ color: service.accentColor }}>
                        {item.label}
                      </span>
                    </div>
                    <p className="text-[14px] text-[#8BA3BC] leading-relaxed">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ServicesPageClient() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToService = (id: string) => {
    setMobileOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="bg-navy-900 text-white min-h-screen overflow-x-hidden">
      {/* ── Navbar ── */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "py-3 bg-navy-900/95 backdrop-blur-xl border-b border-white/5 shadow-lg"
            : "py-5"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between">
          <Link href="/home-page" className="flex items-center">
            <AppImage
              src="/assets/images/logo-white-1-1772521738205.png"
              alt="BlueSea Marine Services logo"
              width={160}
              height={62}
              className="h-10 w-auto object-contain"
              priority
            />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8">
            {services.map((s) => (
              <button
                key={s.id}
                onClick={() => scrollToService(s.id)}
                className="text-[12px] uppercase tracking-[0.25em] font-bold text-[#8BA3BC] hover:text-white transition-colors duration-300"
              >
                {s.title}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-4">
            <Link
              href="/home-page#contact"
              className="hidden md:inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-ocean-600 hover:bg-ocean-500 text-white text-[12px] font-bold uppercase tracking-widest transition-all duration-300 hover:-translate-y-0.5"
            >
              Get Quote
            </Link>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden flex flex-col gap-1.5 w-8"
              aria-label="Toggle mobile menu"
            >
              <span className={`w-full h-0.5 bg-white transition-all duration-300 ${mobileOpen ? "rotate-45 translate-y-2" : ""}`} />
              <span className={`h-0.5 bg-white transition-all duration-300 ${mobileOpen ? "opacity-0 w-0" : "w-2/3"}`} />
              <span className={`w-full h-0.5 bg-white transition-all duration-300 ${mobileOpen ? "-rotate-45 -translate-y-2" : ""}`} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      <div
        className={`fixed inset-0 z-40 transition-all duration-500 ${
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-navy-900/98 backdrop-blur-xl" onClick={() => setMobileOpen(false)} />
        <div
          className={`absolute top-0 right-0 h-full w-[80vw] max-w-sm bg-navy-800 border-l border-white/5 p-8 flex flex-col gap-8 transition-transform duration-500 ${
            mobileOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between pt-4">
            <AppImage
              src="/assets/images/logo-white-1-1772521738205.png"
              alt="BlueSea Marine Services logo"
              width={140}
              height={54}
              className="h-9 w-auto object-contain"
            />
            <button onClick={() => setMobileOpen(false)} className="text-[#8BA3BC] hover:text-white transition-colors" aria-label="Close menu">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <div className="flex flex-col gap-2 mt-4">
            {services.map((s, i) => (
              <button
                key={s.id}
                onClick={() => scrollToService(s.id)}
                className="flex items-center gap-4 px-4 py-4 rounded-xl text-[#8BA3BC] hover:text-white hover:bg-white/5 transition-all duration-300 text-[15px] font-bold uppercase tracking-widest"
              >
                <span className="text-[11px] text-ocean-400/60 font-mono">0{i + 1}</span>
                {s.title}
              </button>
            ))}
            <Link
              href="/home-page"
              className="flex items-center gap-4 px-4 py-4 rounded-xl text-[#8BA3BC] hover:text-white hover:bg-white/5 transition-all duration-300 text-[15px] font-bold uppercase tracking-widest"
            >
              ← Back to Home
            </Link>
          </div>
          <div className="mt-auto">
            <Link
              href="/home-page#contact"
              className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-2xl bg-ocean-600 hover:bg-ocean-500 text-white font-bold uppercase tracking-widest text-[13px] transition-all"
            >
              Request Service
            </Link>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <div className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-navy-950 via-navy-900 to-navy-800" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[400px] bg-ocean-700/10 blur-[160px] pointer-events-none rounded-full" />
        {/* Decorative grid */}
        <div
          className="absolute inset-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(33,150,243,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(33,150,243,0.5) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <Link
            href="/home-page"
            className="inline-flex items-center gap-2 text-[12px] uppercase tracking-widest font-bold text-[#8BA3BC] hover:text-white transition-colors mb-10"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" /><path d="m12 19-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-8 h-px bg-ocean-500" />
            <span className="text-[11px] uppercase tracking-[0.4em] text-ocean-400 font-bold">Our Services</span>
          </div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-[0.9] tracking-tight mb-6">
            <span className="text-white">Marine Services</span><br />
            <span className="text-gradient-ocean italic">Built for the Sea</span>
          </h1>
          <p className="text-[#8BA3BC] max-w-xl leading-relaxed text-[17px] mb-12">
            Three core service pillars — waste removal, vessel supply, and independent survey — delivered across 80+ ports in India, UAE, Bangladesh, and Sri Lanka.
          </p>

          {/* Quick-jump cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {services.map((s) => (
              <button
                key={s.id}
                onClick={() => scrollToService(s.id)}
                className="group flex items-center gap-4 p-5 rounded-2xl text-left transition-all duration-300 hover:-translate-y-1"
                style={{
                  background: s.lightColor,
                  border: `1px solid ${s.borderColor}`,
                }}
              >
                <span className="text-2xl">{s.icon}</span>
                <div>
                  <p className="text-white font-bold text-[15px]">{s.title}</p>
                  <p className="text-[12px] mt-0.5" style={{ color: s.accentColor }}>
                    {s.subtitle}
                  </p>
                </div>
                <svg
                  className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity"
                  width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                  style={{ color: s.accentColor }}
                >
                  <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
                </svg>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Service sections ── */}
      {services.map((service, index) => (
        <ServiceSection key={service.id} service={service} index={index} />
      ))}

      {/* ── CTA Banner ── */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-navy-950 via-navy-800 to-navy-950" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[200px] bg-ocean-600/15 blur-[100px] pointer-events-none rounded-full" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 text-center">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-8 h-px bg-ocean-500" />
            <span className="text-[11px] uppercase tracking-[0.4em] text-ocean-400 font-bold">Ready to Start?</span>
            <div className="w-8 h-px bg-ocean-500" />
          </div>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-5">
            Let's Talk About Your Vessel's Needs
          </h2>
          <p className="text-[#8BA3BC] text-[16px] leading-relaxed mb-10 max-w-xl mx-auto">
            Our team is available 24/7 to discuss your requirements and provide a tailored quote for any port in our network.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/home-page#contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-ocean-600 hover:bg-ocean-500 text-white font-bold uppercase tracking-widest text-[13px] transition-all duration-300 hover:-translate-y-0.5"
            >
              Request a Quote
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
              </svg>
            </Link>
            <Link
              href="/home-page"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-white/20 text-white font-bold uppercase tracking-widest text-[13px] transition-all duration-300 hover:border-white/40 hover:-translate-y-0.5"
            >
              Back to Home
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
